import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';

import {
  BAG_FILL_SIZE_TO_KCAL_PER_BAG,
  CODE_TO_NAME_UNKIBBLE,
  DEFAULT_PRICING_MODEL,
  SCOOP_TO_CUP_CONVERSION,
} from './constants';
import { CountryCode, Option, TangoTeamItem, TangoTeamProduct } from './types';
import { getProductPricingAndNutrientInfo } from '../api/getProductPricingAndNutrientInfo';
import { Dropdown } from '../component/Dropdown';
import { MultiSelectDropdown } from '../component/MultiSelectDropdown';
import { TableProductInfo } from '../component/TableProductInfo';
import { countryOptions } from '../dropdownOptions';
import { UnKibbleProductInfoHeaders } from '../tableHeaders';
import {
  BOTTOM_TOAST,
  createRowClickHandler,
  formatPriceWithCurrency,
  generateErrorMsg,
  getDefaultPricingModelOption,
  getPricingModelOptionsForCountry,
} from '../utils';

const UNKIBBLE_PRODUCT_TYPE_ORDER = ['SCOOP', 'SAMPLE', 'REPLACE', 'MEAL'];

export const UnKibbleProductInfo = () => {
  const [unKibbleProduct, setUnKibbleProduct] = useState<TangoTeamProduct[]>();
  const [loading, setLoading] = useState(true);

  const [selectedCountry, setSelectedCountry] = useState<Option>(countryOptions[0]);
  const [selectedPlanSize, setSelectedPlanSize] = useState<Option[]>([]);
  const [selectedRecipe, setSelectedRecipe] = useState<Option[]>([]);
  const [selectedPricingModel, setSelectedPricingModel] = useState<Option>({
    label: DEFAULT_PRICING_MODEL,
    value: DEFAULT_PRICING_MODEL,
  });
  const [selectedFrequency, setSelectedFrequency] = useState<Option[]>([]);
  const [highlightedRows, setHighlightedRows] = useState<Set<number>>(new Set());
  const [lastSelectedRow, setLastSelectedRow] = useState<number | null>(null);

  useEffect(() => {
    getProductPricingAndNutrientInfo({ meal_type: 'UNKIBBLE' })
      .then((res) => {
        setUnKibbleProduct(res.data);
      })
      .catch((err) => {
        toast.error(generateErrorMsg(err), BOTTOM_TOAST);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const { planSizeOptions, recipeOptions, frequencyOptions } = useMemo(() => {
    if (!unKibbleProduct) {
      return {
        planSizeOptions: [],
        recipeOptions: [],
        frequencyOptions: [],
      };
    }

    const planSizeSet = new Set<string>();
    const frequencySet = new Set<string>();

    for (const product of unKibbleProduct) {
      const planId = product?.plan_size?.id;

      if (planId) {
        planSizeSet.add(String(planId));
        frequencySet.add(String(product.frequency));
      }
    }

    const planSizeOptions = Array.from(planSizeSet)
      .map((v) => ({ label: v, value: v }))
      .sort((a, b) => Number(a.value) - Number(b.value));
    const recipeOptions = Object.keys(CODE_TO_NAME_UNKIBBLE).map((key) => ({
      label: key,
      value: key,
    }));

    const frequencyOptions = Array.from(frequencySet)
      .map((v) => ({ label: v, value: v }))
      .sort((a, b) => Number(a.value) - Number(b.value));

    return { planSizeOptions, recipeOptions, frequencyOptions };
  }, [unKibbleProduct]);

  const pricingModelOptions = useMemo(
    () => getPricingModelOptionsForCountry(unKibbleProduct, selectedCountry.value),
    [unKibbleProduct, selectedCountry]
  );

  // Keep the pricing model valid for the selected country
  useEffect(() => {
    if (!pricingModelOptions.length) return;
    if (!pricingModelOptions.some((option) => option.value === selectedPricingModel.value)) {
      setSelectedPricingModel(getDefaultPricingModelOption(pricingModelOptions));
    }
  }, [pricingModelOptions, selectedPricingModel]);

  const filteredProducts = useMemo(() => {
    if (!unKibbleProduct) return [];

    const filtered = unKibbleProduct.filter((product: TangoTeamProduct) => {
      const planSizeMatch =
        !selectedPlanSize.length ||
        selectedPlanSize.some((option) => product?.plan_size?.id.toString() === option.value);

      const recipeMatch =
        !selectedRecipe.length ||
        selectedRecipe.some((option) =>
          product.item_info.some((item) => item.recipe_id === option.value)
        );

      const frequencyMatch =
        !selectedFrequency.length ||
        selectedFrequency.some((option) => String(product.frequency) === option.value);

      return planSizeMatch && recipeMatch && frequencyMatch;
    });

    return filtered.sort((a, b) => {
      const aTypeIndex = UNKIBBLE_PRODUCT_TYPE_ORDER.indexOf(a.type);
      const bTypeIndex = UNKIBBLE_PRODUCT_TYPE_ORDER.indexOf(b.type);

      if (aTypeIndex !== bTypeIndex) {
        if (aTypeIndex === -1) return 1;
        if (bTypeIndex === -1) return -1;
        return aTypeIndex - bTypeIndex;
      }
      return a.code < b.code ? -1 : a.code > b.code ? 1 : 0;
    });
  }, [unKibbleProduct, selectedPlanSize, selectedRecipe, selectedFrequency]);

  // Country is a top level filter, so it is deliberately left untouched here
  const clearFilters = () => {
    setSelectedPlanSize([]);
    setSelectedRecipe([]);
    setSelectedPricingModel(getDefaultPricingModelOption(pricingModelOptions));
    setSelectedFrequency([]);
  };

  const handleRowClick = createRowClickHandler(
    setHighlightedRows,
    setLastSelectedRow,
    lastSelectedRow,
    highlightedRows
  );

  const calculateBagInfo = (itemInfo: TangoTeamItem[], country: string) => {
    const filteredItems = itemInfo.filter((item) => item.country === country);

    let no_of_bags = 0;
    const fill_amount: Set<string> = new Set();
    const calories_per_bag: Set<number> = new Set();
    const packout_definition = [];
    const bag_weight_oz = [];

    for (const item of filteredItems) {
      no_of_bags += item.quantity;
      const skuParts = item.sku.split('-');
      if (skuParts.length < 3) {
        if (item.sku !== 'CLIP')
          toast.error(
            'UnKibbleProductInfo:calculateBagInfo item doesnt have fill amount' + item.sku,
            BOTTOM_TOAST
          );
        continue;
      }
      const amt = Number(skuParts[2]);

      if (amt in BAG_FILL_SIZE_TO_KCAL_PER_BAG) {
        packout_definition.push(item.sku + ' X ' + item.quantity);
        fill_amount.add(amt.toString());
        calories_per_bag.add(BAG_FILL_SIZE_TO_KCAL_PER_BAG[amt]);
        bag_weight_oz.push(CODE_TO_NAME_UNKIBBLE[item.recipe_id] + ': ' + item.weight_oz);
      }
    }

    return {
      no_of_bags,
      fill_amount: [...fill_amount].join(', '),
      calories_per_bag: [...calories_per_bag].join(', '),
      packout_definition,
      bag_weight_oz,
    };
  };

  const firstHeaderGroups = [
    { label: '', colSpan: 7, className: 'bg-slate50' },
    { label: 'Packout Details', colSpan: 1, className: 'bg-amber-100 font-semibold' },
    { label: 'Pricing', colSpan: 3, className: 'bg-green-200 font-semibold' },
    { label: 'Plan Details (UnKibble)', colSpan: 9, className: 'bg-teal-200 font-semibold' },
  ];

  const getScoopInfo = (product: TangoTeamProduct) => {
    const scoop_codes = product?.scoop_codes ?? [];
    const calories_per_scoop = [];
    const scoop_volume_in_ml = [];
    const cup_equivalent = [];
    for (const scoop_code of scoop_codes) {
      calories_per_scoop.push(SCOOP_TO_CUP_CONVERSION[scoop_code]?.[0] || '');
      scoop_volume_in_ml.push(SCOOP_TO_CUP_CONVERSION[scoop_code]?.[1] || '');
      cup_equivalent.push(SCOOP_TO_CUP_CONVERSION[scoop_code]?.[2] || '');
    }
    return {
      calories_per_scoop,
      scoop_volume_in_ml,
      cup_equivalent,
      scoop_code: product?.plan_size?.scoop_code ?? '',
      scoops_per_day: product?.plan_size?.scoops_per_day ?? '',
    };
  };

  const transformProductToTableRow = (product: TangoTeamProduct) => {
    const scoopInfo = getScoopInfo(product);
    const prices = product?.prices?.[selectedPricingModel.value];

    const currency = prices?.currency ? `${prices.currency} $` : '';
    const bagInfo = calculateBagInfo(product.item_info, selectedCountry.value);

    return {
      Code: product.code,
      Description: product.description_bark,
      Type: product.type,
      Recurring: product.recurring.toString(),
      Frequency: product.frequency,
      'Plan Size': product?.plan_size?.id ?? '',
      Status: selectedCountry.value === CountryCode.CA ? product.status_ca : product.status_us,
      'PACKOUT DEFINITION': bagInfo.packout_definition,
      'Price (per DAY)': formatPriceWithCurrency(currency, prices?.price_per_day),
      'Price (per WEEK)': formatPriceWithCurrency(currency, prices?.price_per_week),
      'Price (per SHIPMENT)': formatPriceWithCurrency(currency, prices?.price),
      'FILL AMOUNT # (per bag)': bagInfo.fill_amount,
      'CALORIES (per bag)': bagInfo.calories_per_bag,
      '# of BAGS (per shipment)': bagInfo.no_of_bags,
      'SCOOP SKU #': product?.scoop_codes,
      'CALORIES (per scoop)': scoopInfo?.calories_per_scoop?.[0],
      'SCOOP VOLUME (ml)': scoopInfo?.scoop_volume_in_ml,
      'US Cup Equivalent per Scoop': scoopInfo.cup_equivalent,
      '# of SCOOPS (per day)': scoopInfo.scoops_per_day,
      'BAG WEIGHT (oz)': bagInfo.bag_weight_oz,
    };
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="p-10 flex-shrink-0">
        <h1>UnKibble Product Info</h1>
      </div>

      {!loading && (
        <div className="px-10 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h3 className="text-lg font-semibold mb-4">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end border-b pb-4 mb-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Country</label>
                <Dropdown
                  options={countryOptions}
                  selectedOption={selectedCountry}
                  setSelectedOption={setSelectedCountry}
                  defaultText="Select Country"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium mb-2">Plan Size</label>
                <MultiSelectDropdown
                  options={planSizeOptions}
                  selectedOptions={selectedPlanSize}
                  setSelectedOptions={setSelectedPlanSize}
                  placeholder="Select Plan Sizes"
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium mb-2">Recipe</label>
                <MultiSelectDropdown
                  options={recipeOptions}
                  selectedOptions={selectedRecipe}
                  setSelectedOptions={setSelectedRecipe}
                  placeholder="Select Recipes"
                />
              </div>
              <div className="md:col-span-1">
                <label className="block text-sm font-medium mb-2">Frequency</label>
                <MultiSelectDropdown
                  options={frequencyOptions}
                  selectedOptions={selectedFrequency}
                  setSelectedOptions={setSelectedFrequency}
                  placeholder="Select Frequency"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Pricing Model</label>
                <Dropdown
                  options={pricingModelOptions}
                  selectedOption={selectedPricingModel}
                  setSelectedOption={setSelectedPricingModel}
                  defaultText="Select Pricing Model"
                />
              </div>
              <div className="md:col-span-1">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 underline transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            <div className="mt-4 text-sm text-gray-600">
              Showing {filteredProducts.length} of {unKibbleProduct?.length || 0} products
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          <span className="ml-2">Loading...</span>
        </div>
      ) : (
        <div className="px-10 flex-1 flex flex-col">
          <div className="flex-1 border border-gray-300 rounded-lg">
            <TableProductInfo
              header={UnKibbleProductInfoHeaders}
              data={filteredProducts.map((product: TangoTeamProduct) =>
                transformProductToTableRow(product)
              )}
              topHeaderGroups={firstHeaderGroups}
              highlightedRows={highlightedRows}
              onRowClick={handleRowClick}
            />
          </div>
        </div>
      )}
    </div>
  );
};
